import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import { UIService } from '../types';
import api from '../config/axios';
import { Helmet } from 'react-helmet-async';

const Catalog = () => {
  const [uis, setUis] = useState<UIService[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUIs = async () => {
      try {
        const response = await api.get('/api/catalog');
        // Transform the data to include features array
        const transformedData = response.data.map((ui: any) => ({
          ...ui,
          features: ui.features || [
            'Responsive Design',
            'Modern UI',
            'Easy Customization',
          ],
        }));
        setUis(transformedData);
      } catch (error) {
        console.error('Error fetching catalog:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUIs();
  }, []);

  const filteredUIs = selectedType
    ? uis.filter((ui) => ui.type === selectedType)
    : uis;

  const handleWhatsAppInquiry = (ui: UIService) => {
    const message = `Hi, interested in ${ui.name} for my freelancer gig!`;
    const whatsappUrl = `https://wa.me/1234567890?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading catalog...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>AlonForge Solutions | UI Catalog</title>
        <meta
          name="description"
          content="Browse our collection of UI templates and solutions for VAs, Editors, and SMM professionals"
        />
      </Helmet>

      <div className="min-h-screen bg-slate py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-center mb-8 text-navy font-heading">
            Catalog
          </h1>

          {/* Filter */}
          <div className="mb-8 flex justify-center">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 bg-white"
            >
              <option value="">All Types</option>
              <option value="VA">VA</option>
              <option value="Editor">Editor</option>
              <option value="SMM">SMM</option>
            </select>
          </div>

          {/* Catalog Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUIs.map((ui) => (
              <div
                key={ui.id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-skyblue"
              >
                {ui.image_url && (
                  <img
                    src={ui.image_url}
                    alt={ui.name}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{ui.name}</h3>
                  <p className="text-gray-600 mb-4">{ui.description}</p>
                  <ul className="mb-4 space-y-2">
                    {ui.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckIcon className="h-5 w-5 text-skyblue mr-2" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex space-x-2">
                    <Link
                      to={`/contact?ui=${ui.id}`}
                      className="flex-1 bg-navy text-white text-center px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors font-semibold"
                    >
                      Buy Now
                    </Link>
                    <button
                      onClick={() => handleWhatsAppInquiry(ui)}
                      className="flex-1 bg-skyblue text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors font-semibold"
                    >
                      Inquire
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredUIs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                No items found. Check back soon!
              </p>
            </div>
          )}

          {/* Systems Teaser */}
          <div className="mt-12 bg-white rounded-xl shadow-md p-8 text-center">
            <h3 className="text-2xl font-bold mb-4 text-navy font-heading">
              Need More Than a Portfolio?
            </h3>
            <p className="text-gray-600 mb-6">
              Pair your UI with a custom IT system—CRM, inventory, or automation.
            </p>
            <Link
              to="/systems"
              className="inline-block bg-skyblue text-white px-8 py-3 rounded-lg hover:bg-blue-600 transition-colors font-semibold"
            >
              Explore Systems
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Catalog;

